import type { DateTime } from "luxon";
import { useMemo } from "react";
import type { Department } from "../../../../contexts/AuthProvider";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useLastNameFirst } from "../../../../hooks/useNameFormat";
import { useNurseOvertimeMap } from "../../../../hooks/useNurseOvertimeMap";
import { getShiftDisplayDate } from "../../../../utils/dateUtils";
import { formatPersonFullName } from "../../../../utils/formatters";
import type { HospitalData, Nurse, NurseData, NurseInShift, NurseOvertimeMap, ReplaceShiftRequest } from "../../../../utils/models";
import { ShiftType } from "../../../../utils/models";
import { createDateRange, findDateIndex, generateShiftData } from "../../../../utils/nursePanelUtils";
import { getEmploymentTypeDisplayName } from "../../../../utils/utilFunctions";

export interface UseSwapDataResult {
	originNurseData: NurseData[];
	targetNurseData: NurseData[];
	originShift: NurseInShift | null;
	targetShift: NurseInShift | null;
	originShiftDate: DateTime;
	targetShiftDate: DateTime;
	originShiftType: ShiftType;
	targetShiftType: ShiftType;
}

const findShiftInHospitalData = (
	hospitalData: HospitalData,
	shiftId: string | undefined,
): { shift: NurseInShift | null; weekIndex: number; shiftDate: DateTime; shiftType: ShiftType } | null => {
	if (!shiftId) return null;

	for (let wIdx = 0; wIdx < hospitalData.weeks.length; wIdx++) {
		const week = hospitalData.weeks[wIdx];
		for (let sIdx = 0; sIdx < week.weeklyShifts.length; sIdx++) {
			const shift = week.weeklyShifts[sIdx];
			for (let dIdx = 0; dIdx < shift.days.length; dIdx++) {
				const day = shift.days[dIdx];
				const foundShift = day.nurses.find((n: NurseInShift) => n.id === shiftId);
				if (foundShift) {
					return {
						shift: foundShift,
						weekIndex: wIdx,
						shiftDate: day.date,
						shiftType: sIdx === 0 ? ShiftType.Day : ShiftType.Night,
					};
				}
			}
		}
	}
	return null;
};

const createNurseDataFromShift = (
	shift: NurseInShift,
	nurse: Nurse,
	weekIndex: number,
	hospitalData: HospitalData,
	chosenDepartment: Department,
	nurseOvertimeMap: NurseOvertimeMap | undefined,
	lastNameFirst: boolean,
): NurseData => {
	const dateRange = createDateRange(shift.startTime);
	const date = getShiftDisplayDate(shift.startTime, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes);
	const dayIdx = findDateIndex(dateRange, date);

	const shifts = generateShiftData({
		nurse,
		weekIndex,
		hospitalData,
		sourceDayIndex: dayIdx,
		targetDayIndex: dayIdx,
		chosenDepartment,
	});

	// OT data from backend
	const nurseOtDays = nurseOvertimeMap?.[nurse.id];
	const dateKey = date.toFormat("yyyy-MM-dd");
	const otInfo = nurseOtDays?.[dateKey];
	const overtimeHours = otInfo?.hours ?? 0;

	const dailyHours = shift.endTime && shift.startTime ? Math.max(0, shift.endTime.diff(shift.startTime, "hours").hours) : 0;

	// Use pre-computed cost from backend
	const predictedCostDisplay = `$${shift.cost ?? 0}`;

	return {
		id: nurse.id,
		name: formatPersonFullName(nurse, lastNameFirst),
		role: shift.role,
		predictedCost: predictedCostDisplay,
		nurseType: getEmploymentTypeDisplayName(nurse.employmentType),
		shifts: shifts,
		charge: shift.charge ?? false,
		selectable: false,
		isShiftManager: nurse.isShiftManager,
		canBeReliefCharge: nurse.canBeReliefCharge,
		overtimeHours,
		dailyHours,
		trainingStatus: shift.trainingStatus ?? nurse.trainingStatus,
		shiftTimes: {
			start: shift.startTime,
			end: shift.endTime,
		},
		...(nurse.nursingStartingDate && {
			nurseStartDate: nurse.nursingStartingDate,
		}),
		scheduledDeactivationDate: nurse.scheduledDeactivationDate ?? null,
	};
};

export const useSwapData = (
	isSwapRequest: boolean,
	swapRequest: ReplaceShiftRequest | null,
	requestDate: DateTime,
	requestShift: ShiftType,
): UseSwapDataResult => {
	const { hospitalData, chosenDepartment, timezone, getWeekRange } = useHospitalData();
	const lastNameFirst = useLastNameFirst();
	const { startDate, endDate } = requestDate ? getWeekRange(requestDate) : { startDate: "", endDate: "" };
	const { data: nurseOvertimeMap } = useNurseOvertimeMap(chosenDepartment.department_id, startDate, endDate, timezone, false);

	return useMemo<UseSwapDataResult>(() => {
		if (!isSwapRequest || !swapRequest) {
			return {
				originNurseData: [],
				targetNurseData: [],
				originShift: null,
				targetShift: null,
				originShiftDate: requestDate,
				targetShiftDate: requestDate,
				originShiftType: requestShift,
				targetShiftType: requestShift,
			};
		}

		const originShiftInfo = findShiftInHospitalData(hospitalData, swapRequest.request_for_shift);
		const targetShiftInfo = findShiftInHospitalData(hospitalData, swapRequest.request_target_shift);

		if (!originShiftInfo || !targetShiftInfo || !originShiftInfo.shift || !targetShiftInfo.shift) {
			return {
				originNurseData: [],
				targetNurseData: [],
				originShift: originShiftInfo?.shift || null,
				targetShift: targetShiftInfo?.shift || null,
				originShiftDate: originShiftInfo?.shiftDate || requestDate,
				targetShiftDate: targetShiftInfo?.shiftDate || requestDate,
				originShiftType: originShiftInfo?.shiftType || requestShift,
				targetShiftType: targetShiftInfo?.shiftType || requestShift,
			};
		}

		const originNurse = hospitalData.nurses.find((n) => n.id === originShiftInfo.shift?.nurseId);
		const targetNurse = hospitalData.nurses.find((n) => n.id === targetShiftInfo.shift?.nurseId);

		const createNurseDataArray = (shiftInfo: typeof originShiftInfo, foundNurse: Nurse | undefined) =>
			foundNurse && shiftInfo.shift
				? [createNurseDataFromShift(shiftInfo.shift, foundNurse, shiftInfo.weekIndex, hospitalData, chosenDepartment, nurseOvertimeMap, lastNameFirst)]
				: [];

		return {
			originNurseData: createNurseDataArray(originShiftInfo, originNurse),
			targetNurseData: createNurseDataArray(targetShiftInfo, targetNurse),
			originShift: originShiftInfo.shift,
			targetShift: targetShiftInfo.shift,
			originShiftDate: originShiftInfo.shiftDate,
			targetShiftDate: targetShiftInfo.shiftDate,
			originShiftType: originShiftInfo.shiftType,
			targetShiftType: targetShiftInfo.shiftType,
		};
	}, [isSwapRequest, swapRequest, hospitalData, chosenDepartment, requestDate, requestShift, nurseOvertimeMap, lastNameFirst]);
};
